/**
 * Pricing a roster: what each rostered shift will be paid, and what the client
 * is charged for it.
 *
 * Pay comes from `services/payroll/shiftPay`, the rules payroll itself runs on
 * hours worked. Here they run on hours rostered: paid time is the shift less
 * its unpaid breaks, and billable time is the time a duty is held.
 */

import { allocateOvertime, type OvertimeAllocation, type OvertimeInput } from '../payroll/rules';
import {
  applyStatutoryRateFloor,
  classifyPaidTime,
  priceShift,
  resolveUnsocialPremium,
  statutoryMinimumHourlyRate,
  type FlooredRate,
  type PaidTime,
  type ShiftPay,
} from '../payroll/shiftPay';
import {
  getZonedParts,
  intervalMinutes,
  mergeIntervals,
  roundMoney,
  subtractIntervals,
  sumMinutesForLocalDays,
  zonedDateTimeToUtc,
  type TimeInterval,
} from '../payroll/time';
import type { ResolvedRate } from '../payroll/types';
import { buildChargeRateLookup } from './chargeRates';
import type {
  ChargeRate,
  CostedShift,
  CostingContext,
  MinuteWindow,
  PayBasis,
  PayBreakdown,
  PayScenario,
  PayTrigger,
  ProfitabilitySite,
  RosteredShift,
  RosterPaySettings,
} from './types';

/** Each shift at the rates of its own date, with nothing hypothetical. */
export const AS_ROSTERED: PayBasis = { rateDate: null, scenario: null };

function toIntervals(dateKey: string, windows: MinuteWindow[], timeZone: string): TimeInterval[] {
  return mergeIntervals(windows.map((window) => ({
    start: zonedDateTimeToUtc(dateKey, window.startMinute, timeZone),
    end: zonedDateTimeToUtc(dateKey, window.endMinute, timeZone),
  })));
}

/**
 * The rate somebody would be on if the minimum moved. `referenceFloor` is the
 * minimum they are measured against today: the statutory one where the country
 * has a schedule, the company's base rate elsewhere.
 */
export function scenarioHourlyRate(hourlyRate: number, scenario: PayScenario, referenceFloor: number): number {
  if (!scenario.keepDifferentials) return Math.max(hourlyRate, scenario.minimumHourlyRate);

  const lift = Math.max(0, scenario.minimumHourlyRate - referenceFloor);
  return Math.max(hourlyRate + lift, scenario.minimumHourlyRate);
}

function resolveBasisRate(
  shift: RosteredShift,
  site: ProfitabilitySite,
  context: CostingContext,
  basis: PayBasis,
): FlooredRate {
  const rateDate = basis.rateDate ?? shift.dateKey;
  const floored = applyStatutoryRateFloor(
    context.resolveRate(shift.guardId, rateDate),
    site.countryCode,
    rateDate,
  );
  if (!basis.scenario) return floored;

  const referenceFloor = statutoryMinimumHourlyRate(site.countryCode, rateDate)
    ?? context.settings.baseHourlyRateEur;
  return {
    ...floored,
    rate: {
      ...floored.rate,
      baseHourlyRate: scenarioHourlyRate(floored.rate.baseHourlyRate, basis.scenario, referenceFloor),
    },
  };
}

interface Draft {
  shift: RosteredShift;
  site: ProfitabilitySite;
  floored: FlooredRate;
  paidTime: PaidTime;
  workedIntervals: TimeInterval[];
  dutyIntervals: TimeInterval[];
  unsocialPremium: number;
}

function allocateByGuard(drafts: Draft[], settings: RosterPaySettings): Map<string, OvertimeAllocation> {
  const byGuard = new Map<string, Draft[]>();
  drafts.forEach((draft) => {
    if (!byGuard.has(draft.shift.guardId)) byGuard.set(draft.shift.guardId, []);
    byGuard.get(draft.shift.guardId)!.push(draft);
  });

  const allocations = new Map<string, OvertimeAllocation>();
  byGuard.forEach((guardDrafts) => {
    const inputs: OvertimeInput[] = guardDrafts.map((draft) => ({
      id: draft.shift.id,
      dateKey: draft.shift.dateKey,
      timeZone: draft.site.timeZone,
      startedAtMs: draft.workedIntervals[0]?.start.getTime() ?? 0,
      // As in payroll: a minimum-shift top-up is pay, not time worked.
      paidMinutes: draft.paidTime.payableMinutes,
    }));

    allocateOvertime(inputs, {
      enabled: settings.overtimeEnabled,
      dailyThresholdMinutes: settings.overtimeDailyThresholdMinutes,
      weeklyThresholdMinutes: settings.overtimeWeeklyThresholdMinutes,
      doubleTimeEnabled: settings.doubleTimeEnabled,
      doubleTimeDailyThresholdMinutes: settings.doubleTimeDailyThresholdMinutes,
      weekStartWeekday: settings.overtimeWeekStartWeekday,
    }).forEach((allocation, id) => allocations.set(id, allocation));
  });

  return allocations;
}

const hourly = (minutes: number, rate: number, multiplier = 1): number => (minutes / 60) * rate * multiplier;

/** Splits gross pay into its plain-rate part and what each premium added; the parts sum to the total. */
function breakDownPay(
  pay: ShiftPay,
  paidTime: PaidTime,
  allocation: OvertimeAllocation,
  rate: ResolvedRate,
): PayBreakdown {
  const overtimePremium = roundMoney(
    hourly(allocation.dailyOvertimeMinutes, rate.baseHourlyRate, rate.overtimeDailyMultiplier - 1)
    + hourly(allocation.weeklyOvertimeMinutes, rate.baseHourlyRate, rate.overtimeWeeklyMultiplier - 1)
    + hourly(allocation.doubleTimeMinutes, rate.baseHourlyRate, rate.doubleTimeMultiplier - 1),
  );
  const minimumShiftTopUp = roundMoney(hourly(paidTime.minimumTopUpMinutes, rate.baseHourlyRate));

  return {
    basePay: roundMoney(pay.estimatedBasePay - overtimePremium - minimumShiftTopUp),
    overtimePremium,
    sundayPremium: pay.sundayPremiumPay,
    publicHolidayPremium: pay.publicHolidayPremiumPay,
    unsocialPremium: pay.unsocialPremiumPay,
    minimumShiftTopUp,
  };
}

function buildTriggers(
  draft: Draft,
  allocation: OvertimeAllocation,
  breakdown: PayBreakdown,
  holidayName: string | null,
): PayTrigger[] {
  const { paidTime, floored } = draft;
  const { rate } = floored;
  const triggers: PayTrigger[] = [];

  if (paidTime.minimumTopUpMinutes > 0) {
    triggers.push({
      kind: 'minimum_shift_pay',
      amount: breakdown.minimumShiftTopUp,
      minutes: paidTime.minimumTopUpMinutes,
      label: null,
    });
  }
  if (paidTime.unsocialQualified && draft.unsocialPremium > 0) {
    triggers.push({
      kind: 'unsocial_premium',
      amount: breakdown.unsocialPremium,
      minutes: paidTime.unsocialMinutes,
      label: null,
    });
  }
  if (paidTime.sundayMinutes > 0 && rate.sundayPremiumMultiplier > 0) {
    triggers.push({
      kind: 'sunday_premium',
      amount: breakdown.sundayPremium,
      minutes: paidTime.sundayMinutes,
      label: null,
    });
  }
  if (paidTime.publicHolidayMinutes > 0 && rate.publicHolidayMultiplier > 0) {
    triggers.push({
      kind: 'public_holiday_premium',
      amount: breakdown.publicHolidayPremium,
      minutes: paidTime.publicHolidayMinutes,
      label: holidayName,
    });
  }
  if (allocation.overtimeMinutes > 0) {
    triggers.push({
      kind: 'overtime',
      amount: roundMoney(
        hourly(allocation.dailyOvertimeMinutes, rate.baseHourlyRate, rate.overtimeDailyMultiplier - 1)
        + hourly(allocation.weeklyOvertimeMinutes, rate.baseHourlyRate, rate.overtimeWeeklyMultiplier - 1),
      ),
      minutes: allocation.overtimeMinutes,
      label: null,
    });
  }
  if (allocation.doubleTimeMinutes > 0) {
    triggers.push({
      kind: 'double_time',
      amount: roundMoney(hourly(allocation.doubleTimeMinutes, rate.baseHourlyRate, rate.doubleTimeMultiplier - 1)),
      minutes: allocation.doubleTimeMinutes,
      label: null,
    });
  }
  if (floored.floorApplied) {
    triggers.push({
      kind: 'statutory_rate_floor',
      amount: roundMoney(hourly(paidTime.paidMinutes, rate.baseHourlyRate - floored.resolvedHourlyRate)),
      minutes: paidTime.paidMinutes,
      label: null,
    });
  }

  return triggers;
}

/** The first public holiday the shift's paid time touches. */
function holidayNameFor(workedIntervals: TimeInterval[], timeZone: string, holidays: Map<string, string>): string | null {
  for (const interval of workedIntervals) {
    const startName = holidays.get(getZonedParts(interval.start, timeZone).dateKey);
    if (startName) return startName;
    // The last paid instant, not the end itself: a shift ending at midnight does not touch the next day.
    const endName = holidays.get(getZonedParts(new Date(interval.end.getTime() - 1), timeZone).dateKey);
    if (endName) return endName;
  }
  return null;
}

interface Billing {
  billableMinutes: number;
  weightedBillableHours: number;
  revenue: number | null;
}

function billShift(
  dutyIntervals: TimeInterval[],
  timeZone: string,
  chargeRate: ChargeRate | null,
  holidays: Map<string, string>,
): Billing {
  const billableMinutes = Math.round(intervalMinutes(dutyIntervals));
  const publicHolidayMinutes = sumMinutesForLocalDays(dutyIntervals, timeZone, (key) => holidays.has(key));
  // A public holiday that falls on a Sunday is charged as the holiday, once.
  const sundayMinutes = sumMinutesForLocalDays(
    dutyIntervals,
    timeZone,
    (key, weekdayIndex) => weekdayIndex === 0 && !holidays.has(key),
  );
  const plainMinutes = billableMinutes - publicHolidayMinutes - sundayMinutes;

  const weightedBillableHours = (
    plainMinutes
    + (sundayMinutes * (chargeRate?.sundayMultiplier ?? 1))
    + (publicHolidayMinutes * (chargeRate?.publicHolidayMultiplier ?? 1))
  ) / 60;

  return {
    billableMinutes,
    weightedBillableHours,
    revenue: chargeRate ? roundMoney(weightedBillableHours * chargeRate.hourlyRate) : null,
  };
}

/**
 * Price every shift. Overtime belongs to a guard's whole week, so pass every
 * shift of the weeks a period touches and filter the result to the period;
 * pricing a week's shifts in isolation would understate it.
 *
 * Shifts at a site the context does not know are left out.
 */
export function costRoster(
  shifts: RosteredShift[],
  context: CostingContext,
  basis: PayBasis = AS_ROSTERED,
): CostedShift[] {
  const chargeRateFor = buildChargeRateLookup(context.chargeRates);
  const isPublicHoliday = (key: string) => context.holidays.has(key);

  const drafts: Draft[] = [];
  shifts.forEach((shift) => {
    const site = context.sites.get(shift.siteId);
    if (!site) return;

    const floored = resolveBasisRate(shift, site, context, basis);
    const span = toIntervals(shift.dateKey, [shift], site.timeZone);
    const workedIntervals = subtractIntervals(
      span,
      toIntervals(shift.dateKey, shift.unpaidBreakWindows, site.timeZone),
    );
    const rateDate = basis.rateDate ?? shift.dateKey;

    drafts.push({
      shift,
      site,
      floored,
      workedIntervals,
      dutyIntervals: toIntervals(shift.dateKey, shift.dutyWindows, site.timeZone),
      paidTime: classifyPaidTime(workedIntervals, {
        timeZone: site.timeZone,
        countryCode: site.countryCode,
        rate: floored.rate,
        settings: context.settings,
        isPublicHoliday,
      }),
      unsocialPremium: basis.scenario?.unsocialShiftPremium
        ?? resolveUnsocialPremium(context.settings, floored.rate, rateDate, site.countryCode),
    });
  });

  const allocations = allocateByGuard(drafts, context.settings);

  return drafts.map((draft) => {
    const { shift, site, floored, paidTime } = draft;
    const allocation = allocations.get(shift.id) ?? {
      id: shift.id,
      ordinaryMinutes: paidTime.payableMinutes,
      dailyOvertimeMinutes: 0,
      weeklyOvertimeMinutes: 0,
      overtimeMinutes: 0,
      doubleTimeMinutes: 0,
    };
    const pay = priceShift(paidTime, allocation, floored.rate, draft.unsocialPremium);
    const breakdown = breakDownPay(pay, paidTime, allocation, floored.rate);
    const chargeRate = chargeRateFor(site, basis.rateDate ?? shift.dateKey);

    return {
      shift,
      site,
      hourlyRate: floored.rate.baseHourlyRate,
      usedFallbackRate: floored.rate.isFallback,
      payableMinutes: paidTime.payableMinutes,
      minimumTopUpMinutes: paidTime.minimumTopUpMinutes,
      paidMinutes: paidTime.paidMinutes,
      overtimeMinutes: allocation.overtimeMinutes,
      doubleTimeMinutes: allocation.doubleTimeMinutes,
      sundayMinutes: paidTime.sundayMinutes,
      publicHolidayMinutes: paidTime.publicHolidayMinutes,
      unsocialMinutes: paidTime.unsocialMinutes,
      unsocialQualified: paidTime.unsocialQualified,
      pay,
      breakdown,
      triggers: buildTriggers(
        draft,
        allocation,
        breakdown,
        holidayNameFor(draft.workedIntervals, site.timeZone, context.holidays),
      ),
      chargeRate,
      ...billShift(draft.dutyIntervals, site.timeZone, chargeRate, context.holidays),
    };
  });
}

/** The shifts whose roster date falls inside a period, both ends included. */
export function withinPeriod(costed: CostedShift[], startDate: string, endDate: string): CostedShift[] {
  return costed.filter(({ shift }) => shift.dateKey >= startDate && shift.dateKey <= endDate);
}
